/**
 * The route table.
 *
 * Single source of truth for navigation, breadcrumbs, deep links and the
 * authorization module list. Defined here rather than in the web app because
 * the API also needs to know which routes exist in order to return
 * `allowedActions` that point somewhere real.
 *
 * Where the UX Specification and Prompt 3 disagreed on a route shape, the
 * Prompt 3 form is used — it is the more recent and more specific document, and
 * it is the one that carries tab and id parameters (see PVI_Understanding.md
 * §16 A8).
 */

export const ROUTES = {
  executive: '/executive',

  portfolioHierarchy: '/portfolio/hierarchy',

  discoverCandidates: '/discover/candidates',
  discoverCandidateNew: '/discover/candidates/new',
  discoverWizard: '/discover/wizard/:candidateId',
  discoverResults: '/discover/results/:assessmentId',

  products: '/products',
  product360: '/products/:productId/:tab?',

  mappingProcessProduct: '/mapping/process-product',
  mappingApplicationProduct: '/mapping/application-product',

  applications: '/applications',
  applicationDetail: '/applications/:applicationId',

  dataImport: '/data/import',
  dataImportSession: '/data/import/:sessionId',
  dataRun: '/data/runs/:runId',
  dataExports: '/data/exports',

  delivery: '/delivery',
  investment: '/investment',
  workforce: '/workforce',
  suppliers: '/suppliers',
  contracts: '/contracts',

  scenarioCompare: '/scenarios/:scenarioId/compare',
  scenarios: '/scenarios',

  adminIntegrations: '/admin/integrations',
  adminLineage: '/admin/lineage/:datasetId?',
  adminCommandCenter: '/admin/command-center',
} as const;

export type RouteKey = keyof typeof ROUTES;

/** Navigation groups, in rail order. Mirrors the reference screens' left rail. */
export interface NavItem {
  key: RouteKey;
  label: string;
  /** Icon token resolved by the design system; never a raw asset path. */
  icon: string;
  /** Module name used by the authorization policy to include or omit the item. */
  module: string;
  /** Depth tier from the build strategy — drives the "scaffold" badge. */
  tier: 1 | 2 | 3;
}

export interface NavGroup {
  id: string;
  label: string | null;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    id: 'overview',
    label: null,
    items: [
      { key: 'executive', label: 'Executive Summary', icon: 'gauge', module: 'portfolio', tier: 3 },
      {
        key: 'portfolioHierarchy',
        label: 'Portfolio Hierarchy',
        icon: 'hierarchy',
        module: 'portfolio',
        tier: 1,
      },
    ],
  },
  {
    id: 'discover',
    label: 'Product Discovery',
    items: [
      {
        key: 'discoverCandidates',
        label: 'Product Candidates',
        icon: 'compass',
        module: 'product-definition',
        tier: 1,
      },
      { key: 'products', label: 'Products', icon: 'cube', module: 'product-360', tier: 1 },
    ],
  },
  {
    id: 'mapping',
    label: 'Mapping',
    items: [
      {
        key: 'mappingProcessProduct',
        label: 'Process Mapping',
        icon: 'flow',
        module: 'relationship-mapping',
        tier: 1,
      },
      {
        key: 'mappingApplicationProduct',
        label: 'Application Mapping',
        icon: 'grid',
        module: 'relationship-mapping',
        tier: 1,
      },
      { key: 'applications', label: 'Applications', icon: 'apps', module: 'application', tier: 1 },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      {
        key: 'delivery',
        label: 'Delivery & Flow',
        icon: 'activity',
        module: 'delivery-reliability',
        tier: 2,
      },
      { key: 'investment', label: 'Investment & Value', icon: 'coins', module: 'value', tier: 2 },
      { key: 'workforce', label: 'Workforce', icon: 'people', module: 'value', tier: 3 },
      { key: 'suppliers', label: 'Suppliers', icon: 'truck', module: 'value', tier: 3 },
      { key: 'contracts', label: 'Contracts', icon: 'contract', module: 'value', tier: 3 },
      {
        key: 'scenarios',
        label: 'Scenario Modeling',
        icon: 'sliders',
        module: 'scenario',
        tier: 2,
      },
    ],
  },
  {
    id: 'data',
    label: 'Data',
    items: [
      { key: 'dataImport', label: 'Import Center', icon: 'upload', module: 'ingestion', tier: 1 },
      {
        key: 'adminIntegrations',
        label: 'Integrations',
        icon: 'plug',
        module: 'ingestion',
        tier: 2,
      },
      {
        key: 'adminLineage',
        label: 'Lineage & Quality',
        icon: 'lineage',
        module: 'ingestion',
        tier: 2,
      },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    items: [
      {
        key: 'adminCommandCenter',
        label: 'Command Center',
        icon: 'shield',
        module: 'governance',
        tier: 2,
      },
    ],
  },
];

/** Replace `:params` in a route template. Optional segments are dropped when absent. */
export function buildPath(
  template: string,
  params: Record<string, string | undefined> = {},
): string {
  return template
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;
      const optional = segment.endsWith('?');
      const name = segment.slice(1, optional ? -1 : undefined);
      const value = params[name];
      if (value === undefined) {
        if (optional) return null;
        throw new Error(`buildPath: missing required parameter "${name}" for "${template}"`);
      }
      return encodeURIComponent(value);
    })
    .filter((segment): segment is string => segment !== null)
    .join('/');
}
